import {configureStore, type EnhancedStore, type Reducer} from '@reduxjs/toolkit'
import {createBrowserHistory} from 'history'

import {Api} from '../api/Api'
import {loadState, saveState} from './local-storage'
import {type AppState, toPersistableAppState} from './types/app-state'
import {type PersistableAppState} from './types/persistable-app-state'
import {INITIAL_STORE} from './types/initial-store'
import rootReducer from './root-reducer'

interface AsyncReducers {
    [key: string]: Reducer
}
type InjectReducerFunction = (key: string, reducer: Reducer) => void

export const history = createBrowserHistory()
export let api: Api

export default function configureGlobalStore(): EnhancedStore<AppState> {
    const persistedStore: PersistableAppState | undefined =
        import.meta.env.JEST_WORKER_ID === undefined ? loadState() : undefined
    api = new Api()

    const preloadedState = {...INITIAL_STORE, ...persistedStore}

    const asyncReducers: AsyncReducers = {}

    const createDynamicReducer = (): Reducer => {
        return rootReducer(asyncReducers)
    }

    const store = configureStore({
        reducer: createDynamicReducer(),
        preloadedState,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                thunk: {
                    extraArgument: api,
                },
                serializableCheck: false,
            }),
    })

    const injectReducer: InjectReducerFunction = (key, reducer) => {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!asyncReducers[key]) {
            asyncReducers[key] = reducer
            store.replaceReducer(createDynamicReducer())
        }
    }

    const enhancedStore = store as EnhancedStore<AppState> & {
        injectReducer: InjectReducerFunction
    }
    enhancedStore.injectReducer = injectReducer
    enhancedStore.subscribe(() => {
        saveState(toPersistableAppState(enhancedStore.getState()))
    })

    api.init(enhancedStore)

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (persistedStore?.auth?.authToken === undefined) {
        api.initWs()
    }

    return enhancedStore
}

export const injectReducer = (
    key: string,
    asyncReducer: Reducer,
    store: EnhancedStore<AppState> & {injectReducer?: InjectReducerFunction},
): void => {
    if (store.injectReducer) {
        store.injectReducer(key, asyncReducer)
    }
}
